import React, { ReactNode } from 'react';
import { View, ViewStyle } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { Text } from '@/components/ui/text';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { colors } from '../theme';

export interface PageHeaderProps {
  /**
   * The title text to display
   */
  title: string;

  /**
   * Optional subtitle text to display below the title
   */
  subtitle?: string;

  /**
   * Optional Material icon name to display before the title
   */
  icon?: keyof typeof MaterialIcons.glyphMap;

  /**
   * Optional action button/element on the left side (e.g., back button)
   */
  action?: ReactNode;

  /**
   * Optional action button/element on the right side (e.g., notification bell)
   */
  rightAction?: ReactNode;

  /**
   * Background color of the header
   * @default 'white'
   */
  backgroundColor?: string;

  /**
   * Text color for title and icon
   * @default colors.text.primary
   */
  textColor?: string;

  /**
   * Icon color (defaults to textColor if not specified)
   */
  iconColor?: string;

  /**
   * Additional styles for the container
   */
  style?: ViewStyle;

  /**
   * Whether to include safe area padding at the top
   * @default true
   */
  includeSafeArea?: boolean;
}

/**
 * PageHeader - Reusable header component for all screens
 *
 * Provides consistent header styling across all platforms with:
 * - Optional leading icon
 * - Title text
 * - Optional action button
 * - Safe area handling
 * - Customizable colors
 *
 * @example
 * ```tsx
 * // Simple header
 * <PageHeader title="ICF Progress" />
 *
 * // With icon and colored background
 * <PageHeader
 *   title="Chat"
 *   icon="chat"
 *   backgroundColor={colors.primary}
 *   textColor="white"
 * />
 *
 * // With action button
 * <PageHeader
 *   title="Documents"
 *   icon="folder"
 *   action={
 *     <Button onPress={handleUpload}>
 *       <Text>Upload</Text>
 *     </Button>
 *   }
 * />
 * ```
 */
export function PageHeader({
  title,
  icon,
  action,
  rightAction,
  backgroundColor = 'white',
  textColor = colors.text.primary,
  iconColor,
  style,
  includeSafeArea = true,
}: PageHeaderProps) {
  const insets = useSafeAreaInsets();
  const finalIconColor = iconColor || textColor;

  return (
    <View
      style={[
        {
          backgroundColor,
          paddingTop: includeSafeArea ? Math.max(insets.top, 16) : 16,
          paddingBottom: 16,
          paddingHorizontal: 16,
          borderBottomWidth: backgroundColor === 'white' || backgroundColor === '#FFFFFF' ? 1 : 0,
          borderBottomColor: colors.border,
        },
        style,
      ]}
    >
      <View className="flex-row items-center justify-between">
        <View className="flex-row items-center flex-1">
          {icon && (
            <View className="mr-3">
              <MaterialIcons name={icon} size={24} color={finalIconColor} />
            </View>
          )}
          <Text
            className="text-xl font-semibold flex-1"
            style={{ color: textColor }}
            numberOfLines={1}
          >
            {title}
          </Text>
        </View>
        {action && (
          <View className="ml-3">
            {action}
          </View>
        )}
        {rightAction && (
          <View className="ml-3">
            {rightAction}
          </View>
        )}
      </View>
    </View>
  );
}

export default PageHeader;
